import type { BundleInventoryItem } from "@internal/backstage-plugin-avd-common";
import { isHoldActive } from "@internal/backstage-plugin-avd-common";

import {
	bundleIacVersion,
	descriptorFor,
	type BundleInventoryDescriptor,
} from "./bundleDescriptors";
import { deriveHealth, type BundleHealth, type HealthReason } from "./bundleHealth";
import { ownerLabel } from "./inventoryCells";
import type { TileId } from "./inventoryFilters";

/** What the hero and the tiles say about the whole read; never about the filtered rows. */
export type EstateMeasures = {
	total: number;
	colleagues: number;
	counts: Record<TileId, number>;
};

export function estateMeasures(
	items: readonly BundleInventoryItem[],
	descriptors: readonly BundleInventoryDescriptor[],
	now: Date,
): EstateMeasures {
	const counts: Record<TileId, number> = { ready: 0, attention: 0, expiring: 0, behind: 0 };
	for (const item of items) {
		const descriptor = descriptorFor(descriptors, item.bundleType);
		if (descriptor.matchesBucket("ready", item)) counts.ready++;
		if (deriveHealth(item, descriptor, now).level !== "healthy") counts.attention++;
		if (item.lifecycle === "expiring") counts.expiring++;
		if (bundleIacVersion(item, descriptor).state === "behind") counts.behind++;
	}
	return { total: items.length, colleagues: new Set(items.map((i) => i.slug)).size, counts };
}

export const ATTENTION_LIMIT = 5;

export type AttentionEntry = {
	item: BundleInventoryItem;
	health: BundleHealth;
	/** The worst reason that is not `info`. */
	worst: HealthReason;
	/** How many further reasons that are not `info`. */
	more: number;
};

/** Bundles that need a decision, failed first, then attention; `total` counts them all, `entries` the first few. */
export function needsAttention(
	items: readonly BundleInventoryItem[],
	descriptors: readonly BundleInventoryDescriptor[],
	now: Date,
	limit = ATTENTION_LIMIT,
): { entries: AttentionEntry[]; total: number } {
	const all = items.flatMap((item): AttentionEntry[] => {
		const health = deriveHealth(item, descriptorFor(descriptors, item.bundleType), now);
		const real = health.reasons.filter((r) => r.level !== "info");
		return health.level === "healthy" || !real.length
			? []
			: [{ item, health, worst: real[0], more: real.length - 1 }];
	});
	all.sort(
		(a, b) =>
			Number(b.health.level === "failed") - Number(a.health.level === "failed") ||
			ownerLabel(a.item).localeCompare(ownerLabel(b.item)),
	);
	return { entries: all.slice(0, limit), total: all.length };
}

export type PrimaryAction =
	| { kind: "run"; href: string }
	| { kind: "hold"; label: "Hold" | "Change hold" };

/** The one action a card offers: a failed bundle's run first, then a hold on an expiring one, then any run. */
export function primaryAction(
	item: BundleInventoryItem,
	health: BundleHealth,
	descriptor: BundleInventoryDescriptor,
	now: Date,
): PrimaryAction | undefined {
	const href = descriptor.runUrl?.(item);
	if (health.level === "failed" && href) return { kind: "run", href };
	if (item.lifecycle === "expiring" && descriptor.hasRegistryRow?.(item) !== false) {
		return { kind: "hold", label: isHoldActive(item.holdUntil, now) ? "Change hold" : "Hold" };
	}
	return href ? { kind: "run", href } : undefined;
}
